<script setup lang="ts">
import { TAOBAO_PURCHASE_URL } from '../purchase'

const macAppleUrl = import.meta.env.VITE_MAC_APPLE_DOWNLOAD_URL || ''
const macIntelUrl = import.meta.env.VITE_MAC_INTEL_DOWNLOAD_URL || ''
const windowsUrl = import.meta.env.VITE_WINDOWS_DOWNLOAD_URL || ''
const releaseVersion = import.meta.env.VITE_DESKTOP_RELEASE_VERSION || '0.4.0'
const macAppleSha256 = import.meta.env.VITE_MAC_APPLE_DOWNLOAD_SHA256 || ''
const macAppleSize = import.meta.env.VITE_MAC_APPLE_DOWNLOAD_SIZE || ''
const macIntelSha256 = import.meta.env.VITE_MAC_INTEL_DOWNLOAD_SHA256 || ''
const macIntelSize = import.meta.env.VITE_MAC_INTEL_DOWNLOAD_SIZE || ''
const windowsSha256 = import.meta.env.VITE_WINDOWS_DOWNLOAD_SHA256 || ''
const windowsSize = import.meta.env.VITE_WINDOWS_DOWNLOAD_SIZE || ''
const windowsSigningStatus = import.meta.env.VITE_WINDOWS_SIGNING_STATUS || '待发布'

// 缩短页面展示的哈希，同时保留完整值供复制和核对。
const formatSha256 = (value: string) => (value ? `${value.slice(0, 12)}…${value.slice(-12)}` : '待生成')
</script>

<template>
  <div class="page-wrap download-page">
    <header class="page-heading">
      <h1>从自有官网下载发多多</h1>
      <p>Windows 与 macOS 安装包只通过 ruwin.cn 官方页面提供。</p>
    </header>
    <section class="download-grid">
      <article class="download-card ml-card">
        <div class="download-card__heading">
          <span class="download-icon">⌘</span>
          <div>
            <h2>macOS Apple 芯片</h2>
            <p>适用于 M1、M2、M3、M4 系列 Mac</p>
          </div>
        </div>
        <div class="download-meta">
          <span class="ml-pill">arm64 · Apple Silicon</span>
          <p>版本 {{ releaseVersion }} · macOS 11.0 Big Sur 及以上</p>
          <p>Developer ID 已签名、公证并装订</p>
          <p v-if="macAppleSize">大小 {{ macAppleSize }}</p>
          <code :title="macAppleSha256">SHA-256 {{ formatSha256(macAppleSha256) }}</code>
        </div>
        <a v-if="macAppleUrl" class="ml-button" :href="macAppleUrl" download>下载 Apple 芯片版</a>
        <button v-else class="ml-button" disabled>安装包待签名后开放</button>
      </article>
      <article class="download-card ml-card">
        <div class="download-card__heading">
          <span class="download-icon">⌘</span>
          <div>
            <h2>macOS Intel</h2>
            <p>适用于 2019 年及以后的 Intel Mac</p>
          </div>
        </div>
        <div class="download-meta">
          <span class="ml-pill">x86_64 · Intel</span>
          <p>版本 {{ releaseVersion }} · macOS 10.15 Catalina 及以上</p>
          <p>Developer ID 已签名、公证并装订</p>
          <p v-if="macIntelSize">大小 {{ macIntelSize }}</p>
          <code :title="macIntelSha256">SHA-256 {{ formatSha256(macIntelSha256) }}</code>
        </div>
        <a v-if="macIntelUrl" class="ml-button" :href="macIntelUrl" download>下载 Intel 版</a>
        <button v-else class="ml-button" disabled>安装包待签名后开放</button>
      </article>
      <article class="download-card ml-card">
        <div class="download-card__heading">
          <span class="download-icon download-icon--teal">⊞</span>
          <div>
            <h2>Windows 版本</h2>
            <p>首批测试覆盖 Windows x64</p>
          </div>
        </div>
        <div class="download-meta">
          <span class="ml-pill ml-pill--teal">x64 · NSIS 安装包</span>
          <p>版本 {{ releaseVersion }} · Windows 10 1803（64 位）及以上</p>
          <p>不支持 Windows 7，安装包已内嵌 WebView2 引导程序</p>
          <p>{{ windowsSigningStatus }}</p>
          <p v-if="windowsSize">大小 {{ windowsSize }}</p>
          <code :title="windowsSha256">SHA-256 {{ formatSha256(windowsSha256) }}</code>
        </div>
        <a v-if="windowsUrl" class="ml-button ml-button--teal" :href="windowsUrl" download>下载 Windows 版</a>
        <button v-else class="ml-button ml-button--teal" disabled>跨平台签名验收后开放</button>
      </article>
    </section>
    <section class="download-security">
      <div>
        <h2>下载与更新安全</h2>
        <ul>
          <li>仅从本页下载，不提供应用商店版本</li>
          <li>更新包和在线规则包验证签名或完整性</li>
          <li>更新失败不破坏当前可用版本</li>
        </ul>
      </div>
      <a data-purchase-link class="ml-button ml-button--secondary" :href="TAOBAO_PURCHASE_URL" target="_blank" rel="noopener noreferrer">前往淘宝购买</a>
    </section>
  </div>
</template>

<style scoped>
.download-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;
}

.download-card {
  display: grid;
  min-height: 380px;
  align-content: space-between;
  padding: 32px;
  box-shadow: var(--ml-shadow);
}

.download-card__heading {
  display: flex;
  gap: 18px;
  align-items: center;
}

.download-card h2 {
  margin: 0 0 8px;
  font-size: 23px;
}

.download-card p {
  margin: 0;
  color: var(--ml-text-secondary);
}

.download-meta {
  display: grid;
  gap: 10px;
}

.download-meta .ml-pill {
  justify-self: start;
}

.download-meta code {
  overflow-wrap: anywhere;
  color: var(--ml-text-secondary);
  font: 12px/1.6 ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.download-icon {
  display: grid;
  width: 66px;
  height: 66px;
  place-items: center;
  border-radius: 20px;
  background: var(--ml-brand-soft);
  color: var(--ml-brand);
  font-size: 32px;
}

.download-icon--teal {
  background: var(--ml-teal-soft);
  color: var(--ml-teal);
}

.download-security {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-top: 42px;
  padding: 24px 30px;
  border-radius: 16px;
  background: #edf5ff;
}

.download-security h2 {
  color: #2f80ed;
  font-size: 18px;
}

.download-security li {
  margin: 8px 0;
  font-size: 13px;
}

@media (max-width: 1120px) {
  .download-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 820px) {
  .download-grid {
    grid-template-columns: 1fr;
  }

  .download-security {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
